import React, { useContext, useState, useEffect, useCallback, useReducer } from 'react'
import { Checkbox, Row, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import { PersonContext } from '../PersonContext'
import {
  getPersonChanges,
  getRelationshipsAccessControls,
  removeRelationship,
  removeRelationships,
  restoreRelationship,
  restoreRelationships,
} from '../../../services/personService'
import ConfirmRemoveDialog from './ConfirmRemoveDialog'
import ParentsPanel from './ParentsPanel'
import SpousesPanel from './SpousesPanel'
import ChildrenPanel from './children/ChildrenPanel'
import useNotifications from '../../../hooks/useNotifications'
import AssociationsPanel from './AssociationsPanel'
import { getRelationship } from '../../../services/RelationshipService'
import { deepCopy } from '../../../util/Utils'
import RestoreRelationshipsDialog from './RestoreRelationshipsDialog'

export default function PersonRelationships() {
  usePageView({ page_detail: 'DMConsole:PersonRelationships' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.relationships'))
  const { error } = useNotifications()
  const { currentPerson, fetchCurrentPerson, getPersonMissingRelationshipOwnerInfo } = useContext(PersonContext)
  const [accessControlsState, accessControlsDispatch] = useReducer(accessControlsReducer, {
    accessControls: {},
    fetched: false,
  })
  const [relationshipToRemoveOrRestore, setRelationshipToRemoveOrRestore] = useState()
  const removeDialogOpener = useOverlay()
  const restoreDialogOpener = useOverlay()
  const [selectedRelationshipsToRemove, setSelectedRelationshipsToRemove] = useState([])
  const [selectedRelationshipsToRestore, setSelectedRelationshipsToRestore] = useState([])
  const [tombstonedParentsRelationships, setTombstonedParentsRelationships] = useState([])
  const [tombstonedChildrenRelationships, setTombstonedChildrenRelationships] = useState([])
  const [tombstonedCoupleRelationships, setTombstonedCoupleRelationships] = useState([])
  const [tombstonedAssociationRelationships, setTombstonedAssociationRelationships] = useState([])
  const [showTombstonedRelationships, setShowTombstonedRelationships] = useState(false)

  const getTombstonedRelationships = useCallback(async () => {
    try {
      const isRelationshipRestored = (relationshipType, relationshipId) => {
        return (
          currentPerson?.relationships?.[relationshipType]?.some(
            (relationship) => relationship.relationshipId === relationshipId
          ) ||
          (relationshipType === 'children' &&
            currentPerson?.relationships?.children?.some((relationship) =>
              relationship.children.some((child) => child.relationshipId === relationshipId)
            ))
        )
      }
      // Get person changes
      const personChangesResponse = await getPersonChanges(currentPerson.id)
      // Look for changes where relationship was tombstoned, and have not been restored
      const uniqueTombstonedRelationships = personChangesResponse.data.changes
        .filter((change) => {
          const relationshipId = change.rawPersonChange?.journalEvent?.relationshipId
          const relationshipRestored = {
            ASSOCIATION: isRelationshipRestored('associations', relationshipId),
            PARENT_CHILD:
              isRelationshipRestored('parents', relationshipId) || isRelationshipRestored('children', relationshipId),
            COUPLE: isRelationshipRestored('spouses', relationshipId),
          }
          return (
            change.rawPersonChange.journalEvent.op === 'RELATIONSHIP_TOMBSTONED' &&
            !relationshipRestored[change.rawPersonChange?.journalEvent?.relationshipType]
          )
        })
        .map((relationshipChange) => {
          return {
            relationshipId: relationshipChange.rawPersonChange.journalEvent.relationshipId,
            relationshipType: relationshipChange.rawPersonChange.journalEvent.relationshipType
              .toLowerCase()
              .replace('_', ''),
          }
        })
        .reduce((accumulator, current) => {
          if (!accumulator.some((item) => item.relationshipId === current.relationshipId)) {
            accumulator.push(current)
          }
          return accumulator
        }, [])

      // Get relationship data for each tombstoned relationship
      const relationshipsResponses = await Promise.allSettled(
        uniqueTombstonedRelationships.map((uniqueRelationship) => {
          try {
            return getRelationship(uniqueRelationship.relationshipType, uniqueRelationship.relationshipId)
          } catch (err) {
            error(
              t('person.relationships.errors.gettingTombstonedRelationship', {
                defaultValue: `Error getting tombstoned relationship: ${err.message || ''}`,
                message: err.message || '',
              }),
              err
            )
            return Promise.reject(err)
          }
        })
      )
      // Add relationship data to lists grouped by relationship type
      const parentsRelationships = []
      const childrenRelationships = []
      const coupleRelationships = []
      const associationRelationships = []
      relationshipsResponses.forEach((relationshipResponse) => {
        if (relationshipResponse.status === 'fulfilled') {
          const relationship = relationshipResponse?.value?.data
          if (relationship) {
            if (relationship.spouse1) {
              coupleRelationships.push(relationship)
            } else if (relationship.parent1) {
              if (relationship.child.personId === currentPerson.id) {
                parentsRelationships.push(relationship)
              } else {
                childrenRelationships.push(relationship)
              }
            } else {
              associationRelationships.push(relationship)
            }
          }
        }
      })
      setTombstonedAssociationRelationships(associationRelationships)
      setTombstonedParentsRelationships(parentsRelationships)
      setTombstonedChildrenRelationships(childrenRelationships)
      setTombstonedCoupleRelationships(coupleRelationships)
      // Get Access Control data for person IDs
      const personIds = new Set()
      associationRelationships.forEach((relationship) => {
        personIds.add(relationship.person1.personId)
        personIds.add(relationship.person2.personId)
      })
      parentsRelationships.forEach((relationship) => {
        personIds.add(relationship.parent1.personId)
        personIds.add(relationship.parent2.personId)
      })
      childrenRelationships.forEach((relationship) => {
        personIds.add(relationship.child.personId)
      })
      coupleRelationships.forEach((relationship) => {
        personIds.add(relationship.spouse1.personId)
        personIds.add(relationship.spouse2.personId)
      })
      try {
        const relationshipAccessControls = await getRelationshipsAccessControls(null, personIds)
        accessControlsDispatch({ type: 'ADD_ACCESS_CONTROLS', payload: relationshipAccessControls.data })
      } catch (err) {
        error(
          t('person.relationships.errors.gettingAccessControlsForTombstonedRelationships', {
            defaultValue: `Error getting access controls for tombstoned relationships: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
      }
    } catch (err) {
      error(
        t('person.relationships.errors.gettingTombstonedRelationships', {
          defaultValue: `Error getting tombstoned relationships: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }, [currentPerson.id, currentPerson?.relationships, error, t])

  useEffect(() => {
    getPersonMissingRelationshipOwnerInfo()
  }, [getPersonMissingRelationshipOwnerInfo])

  useEffect(() => {
    if (!accessControlsState.fetched) {
      getRelationshipsAccessControls(currentPerson.relationships)
        .then((response) => {
          accessControlsDispatch({ type: 'SET_ACCESS_CONTROLS', payload: response.data })
        })
        .catch((err) => {
          error(
            t('person.relationships.errors.gettingRelationshipAccessControl', {
              defaultValue: `Error getting relationship Access Control: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    }
  }, [accessControlsState.fetched, currentPerson, error, t])

  useEffect(() => {
    if (
      showTombstonedRelationships &&
      tombstonedParentsRelationships.length === 0 &&
      tombstonedChildrenRelationships.length === 0 &&
      tombstonedCoupleRelationships.length === 0 &&
      tombstonedAssociationRelationships.length === 0
    ) {
      getTombstonedRelationships()
    }
  }, [
    getTombstonedRelationships,
    showTombstonedRelationships,
    tombstonedAssociationRelationships.length,
    tombstonedChildrenRelationships.length,
    tombstonedCoupleRelationships.length,
    tombstonedParentsRelationships.length,
  ])

  const handleRemoveSelected = (selected, type) => {
    const relationships = selected.map((id) => {
      return { id, type }
    })
    setSelectedRelationshipsToRemove(relationships)
    removeDialogOpener.open()
  }
  const handleRestoreSelected = (selected, type) => {
    const relationships = selected.map((id) => {
      return { id, type }
    })
    setSelectedRelationshipsToRestore(relationships)
    restoreDialogOpener.open()
  }

  const handleRemoveRelationships = (reason) => {
    if (relationshipToRemoveOrRestore) {
      removeRelationship(relationshipToRemoveOrRestore.id, relationshipToRemoveOrRestore.type, reason)
        .then(() => {
          removeDialogOpener.close()
          fetchCurrentPerson()
        })
        .catch((err) => {
          removeDialogOpener.close()
          setRelationshipToRemoveOrRestore(undefined)
          error(
            t('person.relationships.errors.removingRelationship', {
              defaultValue: `Error removing a relationship: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    } else {
      removeRelationships(selectedRelationshipsToRemove, reason)
        .then(() => {
          removeDialogOpener.close()
          fetchCurrentPerson()
        })
        .catch((err) => {
          error(
            t('person.relationships.errors.removingRelationship', {
              defaultValue: `Error removing a relationship: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    }
  }

  const handleRestoreRelationships = (reason) => {
    if (relationshipToRemoveOrRestore) {
      restoreRelationship(relationshipToRemoveOrRestore.id, relationshipToRemoveOrRestore.type, reason)
        .then(() => {
          restoreDialogOpener.close()
          fetchCurrentPerson()
        })
        .catch((err) => {
          restoreDialogOpener.close()
          setRelationshipToRemoveOrRestore(undefined)
          error(
            t('person.relationships.errors.removingRelationship', {
              defaultValue: `Error removing a relationship: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    } else {
      restoreRelationships(selectedRelationshipsToRestore, reason)
        .then(() => {
          restoreDialogOpener.close()
          fetchCurrentPerson()
        })
        .catch((err) => {
          error(
            t('person.relationships.errors.restoringRelationship', {
              defaultValue: `Error restoring the relationship: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    }
  }

  const handleRemoveButton = (relationshipId, type) => {
    setRelationshipToRemoveOrRestore({ id: relationshipId, type })
    removeDialogOpener.open()
  }

  const handleRestoreButton = (relationshipId, type) => {
    setRelationshipToRemoveOrRestore({ id: relationshipId, type })
    restoreDialogOpener.open()
  }

  const handleCloseRemoveRelationshipDialog = () => {
    setRelationshipToRemoveOrRestore(undefined)
    removeDialogOpener.close()
  }

  const handleCloseRestoreRelationshipDialog = () => {
    setRelationshipToRemoveOrRestore(undefined)
    restoreDialogOpener.close()
  }

  const panelProps = {
    handleRemoveSelected,
    handleRestoreSelected,
    currentPerson,
    onRemove: handleRemoveButton,
    onRestore: handleRestoreButton,
    accessControls: accessControlsState.accessControls,
    showTombstonedRelationships,
  }

  return (
    <>
      <Row>
        <Checkbox
          color="primary"
          name="showTombstonedRelationships"
          checked={showTombstonedRelationships}
          onChange={(e) => setShowTombstonedRelationships(e.target.checked)}
          label={t('person.relationships.showTombstoned', 'Show tombstoned')}
        />
      </Row>
      <ParentsPanel {...panelProps} tombstonedParentsRelationships={tombstonedParentsRelationships} />
      <SpousesPanel {...panelProps} tombstonedCoupleRelationships={tombstonedCoupleRelationships} />
      <ChildrenPanel {...panelProps} tombstonedChildrenRelationships={tombstonedChildrenRelationships} />
      <AssociationsPanel {...panelProps} tombstonedAssociationRelationships={tombstonedAssociationRelationships} />
      <ConfirmRemoveDialog
        id="removeConfirmationDialog"
        {...removeDialogOpener}
        onClose={handleCloseRemoveRelationshipDialog}
        onRemove={handleRemoveRelationships}
      />
      <RestoreRelationshipsDialog
        id="restoreConfirmationDialog"
        {...restoreDialogOpener}
        onClose={handleCloseRestoreRelationshipDialog}
        onRestore={handleRestoreRelationships}
      />
    </>
  )
}

function accessControlsReducer(oldState, action) {
  const newState = deepCopy(oldState)
  const { payload, type } = action
  switch (type) {
    case 'SET_ACCESS_CONTROLS': {
      newState.accessControls = payload
      newState.fetched = true
      break
    }
    case 'ADD_ACCESS_CONTROLS': {
      Object.entries(payload).forEach(([personId, accessControl]) => {
        if (!Object.prototype.hasOwnProperty.call(newState.accessControls, personId)) {
          newState.accessControls[personId] = accessControl
        }
      })
      break
    }
    default:
      break
  }
  return newState
}
